import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface TeamsPagePayload {
  teams: any[];
  club_logos: Record<string, string>;
  archived_count: number;
  has_owned: boolean;
  has_access: boolean;
  has_cc_role: boolean;
}

export const teamsPagePayloadKey = (showArchived: boolean) =>
  ["teams-page-payload", showArchived] as const;

const TEAMS_PAYLOAD_TIMEOUT_MS = 20_000;

const LAST_USER_KEY = "teams-payload:last-user";
const cacheKey = (userId: string, showArchived: boolean) =>
  `teams-payload:${userId}:${showArchived ? "archived" : "active"}`;

type CachedPayload = { at: number; payload: TeamsPagePayload };

/**
 * Current auth user id, read synchronously from the persisted Supabase session.
 * We cannot await `getSession()` while producing placeholder data, and rendering
 * another account's teams — even briefly — is not acceptable.
 */
function currentUserIdSync(): string | null {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith("sb-") || !k.endsWith("-auth-token")) continue;
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const id = parsed?.user?.id ?? parsed?.currentSession?.user?.id;
      if (id) return id as string;
    }
  } catch {
    /* unreadable session — treat as signed out */
  }
  return null;
}

/** Last good payload for the CURRENTLY signed-in user, if any. */
function readCachedPayload(showArchived: boolean): CachedPayload | null {
  try {
    const userId = localStorage.getItem(LAST_USER_KEY);
    if (!userId) return null;
    // Never hand back a snapshot belonging to a different account on this device.
    const activeUserId = currentUserIdSync();
    if (!activeUserId || activeUserId !== userId) return null;
    const raw = localStorage.getItem(cacheKey(userId, showArchived));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedPayload;
    if (!parsed?.payload?.teams) return null;
    return parsed;
  } catch {
    return null;
  }
}


function writeCachedPayload(
  userId: string,
  showArchived: boolean,
  payload: TeamsPagePayload,
) {
  try {
    const previous = localStorage.getItem(LAST_USER_KEY);
    if (previous && previous !== userId) {
      // Different account on this device — drop the old snapshot.
      localStorage.removeItem(cacheKey(previous, true));
      localStorage.removeItem(cacheKey(previous, false));
    }
    localStorage.setItem(LAST_USER_KEY, userId);
    localStorage.setItem(
      cacheKey(userId, showArchived),
      JSON.stringify({ at: Date.now(), payload } satisfies CachedPayload),
    );
  } catch {
    /* storage full or unavailable — cache is best-effort */
  }
}

export function clearTeamsPayloadCache() {
  try {
    const userId = localStorage.getItem(LAST_USER_KEY);
    if (userId) {
      localStorage.removeItem(cacheKey(userId, true));
      localStorage.removeItem(cacheKey(userId, false));
    }
    localStorage.removeItem(LAST_USER_KEY);
  } catch {
    /* ignore */
  }
}

function withTimeout<T>(promise: PromiseLike<T>, timeoutMs: number, label: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () =>
        reject(
          new Error(
            `${label} timed out. The server is busy right now — this usually clears in a moment.`,
          ),
        ),
      timeoutMs,
    );
  });

  return Promise.race([Promise.resolve(promise), timeout]).finally(() => {
    if (timeoutId) clearTimeout(timeoutId);
  });
}

/**
 * Shared React Query hook for `get_teams_page_payload`.
 * Both `/teams` (auth/redirect gate) and `<TeamsList />` read from the same
 * cache entry so we make ONE round-trip instead of two identical ones.
 *
 * The last good payload is persisted to localStorage and rendered immediately
 * (stale-while-revalidate) so a slow/contended round-trip never shows an empty
 * error screen to coaches who have loaded the page before.
 */
export function useTeamsPagePayload(showArchived: boolean) {
  return useQuery({
    queryKey: teamsPagePayloadKey(showArchived),
    queryFn: async (): Promise<TeamsPagePayload> => {
      const { data, error } = await withTimeout(
        supabase.rpc("get_teams_page_payload", {
          p_show_archived: showArchived,
        }),
        TEAMS_PAYLOAD_TIMEOUT_MS,
        "Loading teams",
      );
      if (error) throw error;
      const p = (data as any) || {};
      const payload: TeamsPagePayload = {
        teams: p.teams || [],
        club_logos: p.club_logos || {},
        archived_count: p.archived_count || 0,
        has_owned: !!p.has_owned,
        has_access: !!p.has_access,
        has_cc_role: !!p.has_cc_role,
      };
      const userId = (await supabase.auth.getSession()).data.session?.user?.id;
      if (userId) writeCachedPayload(userId, showArchived, payload);
      return payload;
    },
    placeholderData: () => readCachedPayload(showArchived)?.payload,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      const msg = (error as any)?.message || "";
      // Contention timeouts are transient — retry once before surfacing an error.
      if (/timed out/i.test(msg)) return failureCount < 1;
      if (/jwt|permission|denied|unauth/i.test(msg)) return false;
      return failureCount < 1;
    },
    retryDelay: 1_500,
  });
}


export function useInvalidateTeamsPage() {
  const qc = useQueryClient();
  return () =>
    qc.invalidateQueries({ queryKey: ["teams-page-payload"], exact: false });
}
